// A dependency-free map for picking a project point (OpenStreetMap tiles). Drag to pan, tap to place the pin.
import { useEffect, useRef, useState } from "react";

const TILE = 256;
const world = (z) => TILE * 2 ** z;
const project = (lat, lng, z) => {
  const s = Math.sin((lat * Math.PI) / 180);
  return { x: ((lng + 180) / 360) * world(z), y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * world(z) };
};
const unproject = (x, y, z) => ({
  lng: (x / world(z)) * 360 - 180,
  lat: (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / world(z)))) * 180) / Math.PI,
});

export function MiniMap({ lat, lng, onPick, height = 300 }) {
  const has = lat != null && lng != null && lat !== "" && lng !== "";
  const [z, setZ] = useState(has ? 16 : 12);
  const [center, setCenter] = useState(() => (has ? { lat: +lat, lng: +lng } : { lat: 32.3754, lng: 15.0925 })); // Misrata
  const [w, setW] = useState(600);
  const box = useRef();
  const drag = useRef(null);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { if (has) setCenter({ lat: +lat, lng: +lng }); }, [lat, lng, has]);

  const c = project(center.lat, center.lng, z);
  const left = c.x - w / 2;
  const top = c.y - height / 2;
  const tiles = [];
  const n = 2 ** z;
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + w) / TILE); tx++) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      tiles.push(<img key={`${tx}-${ty}`} alt="" draggable={false} src={`https://tile.openstreetmap.org/${z}/${wx}/${ty}.png`}
                      style={{ position: "absolute", left: tx * TILE - left, top: ty * TILE - top, width: TILE, height: TILE }} />);
    }
  }
  const pin = has ? project(+lat, +lng, z) : null;

  const down = (e) => { drag.current = { x: e.clientX, y: e.clientY, cx: c.x, cy: c.y, moved: false }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
    if (d.moved) setCenter(unproject(d.cx - dx, d.cy - dy, z));
  };
  const up = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.moved || !onPick) return;
    const r = box.current.getBoundingClientRect();
    const p = unproject(left + (e.clientX - r.left), top + (e.clientY - r.top), z);
    onPick(+p.lat.toFixed(6), +p.lng.toFixed(6));
  };
  return (
    <div className="minimap" dir="ltr" ref={box} style={{ height }} onPointerDown={down} onPointerMove={move} onPointerUp={up}
         role="application" aria-label="خريطة موقع المشروع — اسحب للتحريك واضغط لتحديد النقطة">
      {tiles}
      {pin && <span className="minimap-pin" style={{ left: pin.x - left, top: pin.y - top }} aria-hidden="true" />}
      <div className="minimap-zoom" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => setZ((v) => Math.min(19, v + 1))} aria-label="تكبير">+</button>
        <button type="button" onClick={() => setZ((v) => Math.max(3, v - 1))} aria-label="تصغير">−</button>
      </div>
      <a className="minimap-credit" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap</a>
    </div>
  );
}
